import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { navigate } from "../utils/navigate";
import { api } from "../api";
import { t } from "../i18n";
import BrandLogo from "../components/BrandLogo";
import LangBar from "../components/LangBar";
import LoginHero from "../components/LoginHero";

export default function LoginPage({ lang, setLang }) {
  const { login } = useAuth();
  const [mode, setMode] = useState("otp");
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [staffChallenge, setStaffChallenge] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sentOtp, setSentOtp] = useState("");

  const redirectByRole = (user) => {
    if (user?.is_admin && !user?.is_doctor) navigate("/doctors");
    else if (user?.is_doctor || user?.is_admin) navigate("/board");
    else navigate("/");
  };

  const sendOTP = async () => {
    if (!phone) {
      setError(t(lang, "phone"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await api("/api/auth/send-otp", { method: "POST", body: { phone } });
      if (data.otp) setSentOtp(data.otp);
      setStep("otp");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp) return;
    setLoading(true);
    setError("");
    try {
      const data = await api("/api/auth/verify-otp", { method: "POST", body: { phone, otp } });
      login(data.token, data.user);
      if (data.user?.language) setLang(data.user.language);
      redirectByRole(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const staffLoginStep1 = async () => {
    if (!phone || !password) {
      setError(`${t(lang, "phone")} / ${t(lang, "password")}`);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await api("/api/auth/staff-login", {
        method: "POST",
        body: { phone, password },
      });
      if (data.status === "otp_required" && data.challenge) {
        setStaffChallenge(data.challenge);
        if (data.otp) setSentOtp(data.otp);
        setOtp("");
        setStep("otp");
        return;
      }
      if (data.token && data.user) {
        login(data.token, data.user);
        redirectByRole(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const staffVerifyOtp = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api("/api/auth/staff-verify-otp", {
        method: "POST",
        body: { phone, otp, challenge: staffChallenge },
      });
      login(data.token, data.user);
      if (data.user?.language) setLang(data.user.language);
      redirectByRole(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tt-login">
      <LoginHero lang={lang} />

      <section className="tt-login-panel">
        <BrandLogo height={28} />
        <p className="tt-tagline">{t(lang, "tagline")}</p>
        <LangBar lang={lang} onChange={setLang} />

        <div className="tt-tabs">
          <button
            className={mode === "otp" ? "on" : ""}
            onClick={() => {
              setMode("otp");
              setStep("phone");
              setError("");
              setSentOtp("");
            }}
          >
            {t(lang, "familyLogin")}
          </button>
          <button
            className={mode === "staff" ? "on" : ""}
            onClick={() => {
              setMode("staff");
              setStep("phone");
              setError("");
              setSentOtp("");
            }}
          >
            {t(lang, "staffLogin")}
          </button>
        </div>

        {mode === "otp" && step === "phone" && (
          <>
            <input
              type="tel"
              inputMode="numeric"
              placeholder={t(lang, "phone")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={loading}
            />
            {error && <p className="tt-err">{error}</p>}
            <button className="tt-btn" onClick={sendOTP} disabled={loading}>
              {loading ? "…" : t(lang, "sendOtp")}
            </button>
            <p className="tt-hint">{t(lang, "otpHint")}</p>
          </>
        )}

        {mode === "otp" && step === "otp" && (
          <>
            <p className="tt-hint">
              {phone}{" "}
              <button className="tt-link" onClick={() => setStep("phone")}>
                {t(lang, "change")}
              </button>
            </p>
            {sentOtp && (
              <p className="tt-dev">
                Dev OTP: <strong>{sentOtp}</strong>
              </p>
            )}
            <input
              type="text"
              inputMode="numeric"
              placeholder={t(lang, "enterOtp")}
              value={otp}
              maxLength={6}
              onChange={(e) => setOtp(e.target.value)}
              disabled={loading}
            />
            {error && <p className="tt-err">{error}</p>}
            <button className="tt-btn" onClick={verifyOTP} disabled={loading}>
              {t(lang, "verify")}
            </button>
          </>
        )}

        {mode === "staff" && step !== "otp" && (
          <>
            <input
              type="tel"
              placeholder={t(lang, "phone")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={loading}
            />
            <input
              type="password"
              placeholder={t(lang, "password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => e.key === "Enter" && staffLoginStep1()}
            />
            {error && <p className="tt-err">{error}</p>}
            <button className="tt-btn" onClick={staffLoginStep1} disabled={loading}>
              {t(lang, "continueOtp")}
            </button>
          </>
        )}

        {mode === "staff" && step === "otp" && (
          <>
            {sentOtp && (
              <p className="tt-dev">
                Dev OTP: <strong>{sentOtp}</strong>
              </p>
            )}
            <input
              type="text"
              inputMode="numeric"
              placeholder={t(lang, "enterOtp")}
              value={otp}
              maxLength={6}
              onChange={(e) => setOtp(e.target.value)}
              disabled={loading}
            />
            {error && <p className="tt-err">{error}</p>}
            <button className="tt-btn" onClick={staffVerifyOtp} disabled={loading}>
              {t(lang, "staffVerify")}
            </button>
            <button className="tt-btn ghost" onClick={() => setStep("phone")}>
              {t(lang, "back")}
            </button>
          </>
        )}

        <p className="tt-foot">{t(lang, "neverStop")}</p>
        <button className="tt-link" onClick={() => navigate("/wall")}>
          {t(lang, "seeWall")}
        </button>
      </section>
    </div>
  );
}
